import { ReactNode, WheelEventHandler, useCallback, useEffect } from "react";
import * as React from "react";
import {
  FieldValues,
  UseControllerProps,
  useController
} from "react-hook-form";

import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import { InputBaseComponentProps } from "@mui/material/InputBase";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import type { SxProps } from "@mui/material/styles";

import { kebabCase } from "lodash";

import { getCharacterLimitColor } from "utils/character-limit.util";
import { extractDecimalZeros, isBlank } from "utils/common";

type InputFormFieldProps = {
  id?: string;
  testId?: string;
  name: string;
  label?: ReactNode;
  disabled?: boolean;
  required?: boolean;
  multiline?: boolean;
  readonly?: boolean;
  type?: string;
  placeholder?: string;
  pattern?: string;
  autoFocus?: boolean;
  sx?: SxProps | null; // Temporary solution to reset default SX to null
  suffix?: string;
  helperText?: ReactNode;
  characterLimit?: number;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  inputProps?: InputBaseComponentProps;
  isMoney?: boolean;
  // isMoney only: render an EMPTY (null/undefined/"") value as a blank field
  // instead of "0". Opt-in — a required money field that displays "0" while
  // holding no value looks filled and fails validation invisibly (ENG-1892
  // quick-add file import). Default keeps the historical display.
  blankWhenEmpty?: boolean;
  allowNegative?: boolean;
  minRows?: number;
  isFieldValid?: boolean;
  onFieldValidation?: (name: string, isValid: boolean) => void;
  beforeUpdate?: (value: string | number) => string | number;
  afterUpdate?: (value: string | number) => void;
  afterBlur?: (value: string | number) => void;
  onClick?: React.MouseEventHandler<HTMLDivElement> | undefined;
  autoComplete?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  inputComponent?: any;
  onWheel?: WheelEventHandler;
};

export type RhfInputFormFieldProps<T extends FieldValues> =
  InputFormFieldProps & UseControllerProps<T>;

export default function InputFormField<T extends FieldValues>({
  id,
  testId,
  name,
  label,
  disabled = false,
  control,
  rules = {},
  required = false,
  multiline = false,
  readonly = false,
  autoFocus,
  type,
  placeholder = "",
  helperText,
  characterLimit,
  startAdornment,
  endAdornment,
  suffix,
  isMoney = false,
  blankWhenEmpty = false,
  allowNegative = false,
  minRows = 1,
  isFieldValid = true,
  onFieldValidation,
  sx = { ml: 2, mt: 1 }, // TODO - all layout settings should be injected by parent component/wrapper. Component shoudn't change layout by his setting
  afterUpdate,
  afterBlur,
  inputProps,
  beforeUpdate,
  onClick,
  autoComplete,
  inputComponent,
  onWheel
}: RhfInputFormFieldProps<T>) {
  const [moneyDisplayValue, setMoneyDisplayValue] = React.useState<string>("");
  // While the field is focused, `onChange` is the sole owner of the display
  // string so the value-sync effect must not reformat (see SN-18080).
  const [isMoneyFocused, setIsMoneyFocused] = React.useState<boolean>(false);
  const validate = useCallback(
    (value: any) =>
      Boolean(!required || (required && !isBlank(value))) ||
      `${label || "Field"} is required`,
    [required, label]
  );

  const {
    field: { onChange, onBlur, value, ...field },
    fieldState: { error, invalid }
  } = useController<T>({
    name,
    control,
    rules: {
      validate: validate,
      ...rules
    }
  });

  const isValid = isFieldValid && !Boolean(invalid);

  const usedChars = String(value ?? "").length;
  const remainingChars = characterLimit ? characterLimit - usedChars : null;
  const counterColor =
    remainingChars === null
      ? undefined
      : getCharacterLimitColor(remainingChars);
  const errorMessage = error?.message;

  useEffect(() => {
    if (onFieldValidation) {
      onFieldValidation(name, isValid);
    }
  }, [isValid, onFieldValidation, name]);
  useEffect(() => {
    // Reflect the form value into the display, but never while the user is
    // actively typing — otherwise a parent writing the value back mid-edit
    // (e.g. the hearing aids pricing step round-trips it through redux as a
    // string) reformats the field and swallows what was just typed (SN-18080).
    if (!isMoney || isMoneyFocused) {
      return;
    }
    if (
      blankWhenEmpty &&
      (value === null || value === undefined || value === "")
    ) {
      setMoneyDisplayValue("");
      return;
    }
    const numValue = Number(value) || 0;
    const isNegative = allowNegative && numValue < 0;
    const absValue = Math.abs(numValue);
    const formatted = new Intl.NumberFormat("en-US", {
      style: "decimal",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(absValue);
    setMoneyDisplayValue(`${isNegative ? "-" : ""}${formatted}`);
  }, [value, isMoney, blankWhenEmpty, allowNegative, isMoneyFocused]);

  return (
    <FormControl
      disabled={disabled}
      required={required}
      fullWidth
      error={Boolean(errorMessage)}
      sx={sx}
    >
      <TextField
        data-testid={testId}
        required={required}
        id={id ?? kebabCase(name)}
        label={label}
        type={isMoney ? "text" : type}
        multiline={multiline}
        minRows={minRows}
        placeholder={placeholder}
        autoFocus={autoFocus}
        fullWidth
        disabled={disabled}
        error={Boolean(errorMessage)}
        onClick={onClick}
        autoComplete={autoComplete}
        onWheel={onWheel}
        InputProps={{
          readOnly: readonly,
          startAdornment,
          endAdornment:
            endAdornment ||
            (suffix && (
              <Typography variant="body1" sx={{ pl: 1 }}>
                {suffix}
              </Typography>
            )),
          inputComponent
        }}
        inputProps={inputProps}
        value={isMoney ? moneyDisplayValue : value}
        onChange={(event) => {
          let value: string | number = event.target.value;

          if (isMoney) {
            // Check if user typed a minus sign at the start
            const startsWithMinus = allowNegative && value.startsWith("-");

            // Remove everything except digits and decimal point
            const onlyNumsAndDot = value.replace(/[^0-9.]/g, "");

            // Keep only up to 2 decimal places
            const onlyNumsWithTwoDecimal = onlyNumsAndDot.replace(
              /(\d*\.?\d{0,2}).*/,
              "$1"
            );

            // Handle empty field - allow user to clear and start fresh
            if (onlyNumsWithTwoDecimal === "" && !startsWithMinus) {
              setMoneyDisplayValue("");
              value = 0;
            } else if (startsWithMinus && onlyNumsWithTwoDecimal === "") {
              // Handle the case where user typed just "-" with no digits yet
              setMoneyDisplayValue("-");
              value = 0;
            } else {
              // Parse as positive, then apply sign based on user input
              const parsedValue = parseFloat(onlyNumsWithTwoDecimal) || 0;
              value = startsWithMinus ? -parsedValue : parsedValue;

              // Format for display
              const absValue = Math.abs(value);
              const formatted = new Intl.NumberFormat("en-US", {
                style: "decimal",
                minimumFractionDigits: 0,
                maximumFractionDigits: 2
              }).format(absValue);

              // Preserve trailing zeros while typing (e.g., "1.0", "1.00")
              const decimalZeros = extractDecimalZeros(onlyNumsWithTwoDecimal);

              setMoneyDisplayValue(
                `${startsWithMinus ? "-" : ""}${formatted}${decimalZeros || ""}`
              );
            }
          }

          if (beforeUpdate) {
            value = beforeUpdate(value);
          }

          onChange(value);
          afterUpdate?.(value);
        }}
        onFocus={() => {
          if (isMoney) {
            setIsMoneyFocused(true);
          }
        }}
        onBlur={() => {
          if (isMoney) {
            setIsMoneyFocused(false);
          }
          onBlur();
          if (afterBlur) {
            afterBlur(value);
          }
        }}
        {...field}
      />
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
      {characterLimit && usedChars > 0 && (
        <FormHelperText sx={{ textAlign: "right", color: counterColor }}>
          {usedChars} / {characterLimit}
        </FormHelperText>
      )}
      {Boolean(errorMessage) && (
        <FormHelperText error={Boolean(errorMessage)}>
          {errorMessage}
        </FormHelperText>
      )}
    </FormControl>
  );
}
